import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import FlexxBeginnerCourse from "@/components/FlexxBeginnerCourse";
import FlexxAdvancedCourse from "@/components/FlexxAdvancedCourse";
import FlexxCFDCourse from "@/components/FlexxCFDCourse";
import FlexxCommoditiesCourse from "@/components/FlexxCommoditiesCourse";
import FlexxRoadmap from "@/components/FlexxRoadmap";
import AdminDashboard from "@/components/AdminDashboard";
import AdminAnalytics from "@/components/AdminAnalytics";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import SearchLessons from "@/components/SearchLessons";
import BookmarksPanel, { useBookmarks } from "@/components/BookmarksPanel";
import "@/flexx/flexx.css";
import FlexxLearningHub from "@/flexx/FlexxLearningHub";
import AxelWidget from "@/flexx/AxelWidget";
import { XPNotifications } from "@/flexx/XPSystem";
import CertificateVerifyPage from "@/components/CertificateVerifyPage";
import CertificateDemo from "@/flexx/FlexxCertificate";
import LiveRoom from "@/components/LiveRoom";
import AcademyHub from "@/flexx/AcademyHub";
import FlexxLogoImg from "@/components/FlexxLogoImg";
import LessonsHub from "@/flexx/LessonsHub";
import PwaShell from "@/components/PwaShell";
import PwaSplash from "@/components/PwaSplash";
import BottomNav from "@/components/BottomNav";
import SessionAlerts from "@/flexx/SessionAlerts";
import ToolsHub from "@/flexx/tools/ToolsHub";
import AxelMarketBadge from "@/flexx/AxelMarketBadge";
import AxelIntelHub from "@/flexx/AxelIntelHub";

const B = {
  gold: "#D4A843", goldFaint: "#D4A84318",
  blue: "#4A90D9", blueFaint: "#4A90D918",
  cfd: "#00D4AA", cfdFaint: "#00D4AA18",
  commodities: "#E8883A", commoditiesFaint: "#E8883A18",
  green: "#43B581", red: "#E74C3C",
  bg: "#0D0F13", card: "#13161C",
  border: "#1E222A", white: "#F0F0F0",
  whiteGray: "#A0A8B8", gray: "#6B7280",
};

const inputStyle = {
  width: "100%", padding: "10px 14px", marginBottom: 12, borderRadius: 8,
  border: "1px solid #2A2E36", background: "#0D0F13", color: "#F0F0F0",
  fontSize: 14, outline: "none", boxSizing: "border-box",
};

/* ====== Auth Modal ====== */
const AuthModal = ({ onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [superseded, setSuperseded] = useState(() => {
    try {
      const v = sessionStorage.getItem("flexx_session_superseded") === "1";
      if (v) sessionStorage.removeItem("flexx_session_superseded");
      return v;
    } catch (_) { return false; }
  });

  const switchMode = (m) => { setMode(m); setError(""); setInfo(""); setSuperseded(false); };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setInfo(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
        setInfo("If an account exists for that email, a password reset link has been sent. Check your inbox (and spam folder).");
      } else {
        const r = mode === "login" ? await login(email, password) : await register(name, email, password);
        if (r.success) { onClose?.(); return; }
        setError(r.error);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-modal" style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: B.card, borderRadius: 16, padding: 36, border: `1px solid ${B.border}`, maxWidth: 400, width: "100%", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${B.gold}, transparent)`, borderRadius: "16px 16px 0 0" }} />
        <FlexxLogoImg data-testid="auth-modal-logo" style={{ height: 52, display: "block", margin: "0 auto 10px", filter: `drop-shadow(0 0 14px ${B.gold}55)` }} />
        <div style={{ textAlign: "center", fontSize: 10, letterSpacing: 2, color: B.gold, fontFamily: "monospace", marginBottom: 24 }}>TRADING ACADEMY</div>
        {superseded && (
          <div data-testid="auth-superseded-banner" style={{ background: "#2a1416", border: "1px solid #5a2a2e", borderRadius: 8, padding: "10px 12px", marginBottom: 18, color: "#ff9aa6", fontSize: 12.5, lineHeight: 1.6, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ fontSize: 15 }}>🔒</span>
            <span>You were signed out because this account was just used to sign in on another device. For security, FLEXX accounts are limited to one device at a time. Sign in again to continue.</span>
          </div>
        )}
        {mode !== "forgot" && (
          <div style={{ display: "flex", marginBottom: 24, borderRadius: 8, overflow: "hidden", border: `1px solid ${B.border}` }}>
            {["login", "register"].map(m => (
              <button key={m} type="button" data-testid={`auth-tab-${m}`} onClick={() => switchMode(m)}
                style={{ flex: 1, padding: "10px 0", border: "none", cursor: "pointer", background: mode === m ? B.gold + "20" : "transparent", color: mode === m ? B.gold : B.gray, fontSize: 13, fontWeight: 600 }}>
                {m === "login" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>
        )}
        {mode === "forgot" && (
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <h3 data-testid="forgot-title" style={{ color: B.white, fontSize: 16, margin: "0 0 6px" }}>Reset your password</h3>
            <p style={{ color: B.whiteGray, fontSize: 12, lineHeight: 1.5, margin: 0 }}>Enter your account email and we'll send you a secure reset link.</p>
          </div>
        )}
        <form onSubmit={submit}>
          {mode === "register" && <input data-testid="auth-name-input" type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required style={inputStyle} />}
          <input data-testid="auth-email-input" type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          {mode !== "forgot" && (
            <div style={{ position: "relative", marginBottom: 6 }}>
              <input data-testid="auth-password-input" type={showPwd ? "text" : "password"} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                style={{ ...inputStyle, marginBottom: 0, paddingRight: 42 }} />
              <button type="button" data-testid="password-toggle-btn" onClick={() => setShowPwd(!showPwd)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", padding: 4, color: showPwd ? B.gold : B.gray, fontSize: 14, lineHeight: 1, display: "flex", alignItems: "center" }}>
                {showPwd ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                )}
              </button>
            </div>
          )}
          {mode === "login" && (
            <div style={{ textAlign: "right", marginBottom: 12 }}>
              <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
                style={{ background: "transparent", border: "none", color: B.gold, fontSize: 12, cursor: "pointer", padding: 0 }}>
                Forgot password?
              </button>
            </div>
          )}
          {error && <div data-testid="auth-error" style={{ color: B.red, fontSize: 13, marginBottom: 12, textAlign: "center" }}>{error}</div>}
          {info && <div data-testid="auth-info" style={{ color: B.green, fontSize: 13, marginBottom: 12, textAlign: "center", lineHeight: 1.5 }}>{info}</div>}
          <button data-testid="auth-submit-btn" type="submit" disabled={busy}
            style={{ width: "100%", padding: "12px 0", border: "none", borderRadius: 8, background: B.gold, color: "#000", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, marginTop: mode === "forgot" ? 8 : 0 }}>
            {busy ? "..." : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
          </button>
          {mode === "forgot" && (
            <button type="button" data-testid="forgot-back-btn" onClick={() => switchMode("login")}
              style={{ marginTop: 12, width: "100%", background: "transparent", border: "none", color: B.gray, fontSize: 12, cursor: "pointer" }}>
              Back to sign in
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

/* ====== Access Denied ====== */
const AccessDenied = ({ courseLabel }) => {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20, padding: 24, textAlign: "center" }}>
      <FlexxLogoImg style={{ height: 48 }} />
      <h2 data-testid="access-denied-title" style={{ color: B.gold, margin: 0 }}>Access Required</h2>
      <p style={{ color: B.whiteGray, maxWidth: 400, lineHeight: 1.6 }}>
        You don't have access to the <b>{courseLabel}</b> yet.
        Your access needs to be approved by an administrator.
        Please contact your FLEXX Academy admin.
      </p>
      <button data-testid="access-denied-home-btn" onClick={() => navigate("/")}
        style={{ background: B.gold + "20", border: `1px solid ${B.gold}30`, borderRadius: 8, color: B.gold, padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
        Back to Home
      </button>
    </div>
  );
};

/* ====== Course Card ====== */
const CourseCard = ({ title, subtitle, description, accent, accentFaint, icon, features, buttonText, onClick, testId, locked }) => (
  <div data-testid={testId} onClick={onClick}
    style={{ background: B.card, border: `1px solid ${B.border}`, borderRadius: 16, padding: 32, cursor: "pointer", transition: "all 0.3s ease", flex: 1, minWidth: 300, maxWidth: 520, position: "relative", overflow: "hidden", opacity: locked ? 0.6 : 1 }}
    onMouseEnter={e => { e.currentTarget.style.border = `1px solid ${accent}40`; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 12px 40px ${accent}15`; }}
    onMouseLeave={e => { e.currentTarget.style.border = `1px solid ${B.border}`; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 11, letterSpacing: 2, color: accent, fontFamily: "monospace", marginBottom: 2 }}>{subtitle}</div>
        <h2 style={{ margin: 0, fontSize: 22, color: B.white, fontWeight: 700 }}>{title}</h2>
      </div>
    </div>
    <p style={{ color: B.whiteGray, fontSize: 14, lineHeight: 1.7, margin: "0 0 24px 0" }}>{description}</p>
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
      {features.map((f, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: accent, flexShrink: 0 }} />
          <span style={{ color: B.whiteGray, fontSize: 13 }}>{f}</span>
        </div>
      ))}
    </div>
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", borderRadius: 8, background: accentFaint, border: `1px solid ${accent}30`, color: accent, fontSize: 13, fontWeight: 600 }}>
      {locked ? "Request Access" : buttonText} <span style={{ fontSize: 16 }}>{locked ? "\u{1F512}" : "\u2192"}</span>
    </div>
  </div>
);

/* ====== Home Page ====== */
const Home = () => {
  const { user, hasAccess } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const { bookmarks, bookmarkIds, toggleBookmark } = useBookmarks(user);
  const navigate = useNavigate();

  // Auto-open the auth modal when a session was superseded on another device
  // (or on reload while the flag persists), so the explainer banner is seen.
  React.useEffect(() => {
    const open = () => setShowAuth(true);
    window.addEventListener("flexx:session-superseded", open);
    try { if (sessionStorage.getItem("flexx_session_superseded") === "1") setShowAuth(true); } catch (_) {}
    return () => window.removeEventListener("flexx:session-superseded", open);
  }, []);

  React.useEffect(() => {
    if (!user) return;
    const ax = { withCredentials: true };
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/notifications/unread-count`, ax).then(r => setNotifCount(r.data.count)).catch(() => {});
  }, [user]);

  const openNotifs = async () => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifs(data);
      setShowNotifs(true);
      setNotifCount(0);
    } catch {}
  };

  const go = (path, courseId) => {
    if (!user) { setShowAuth(true); return; }
    navigate(path);
  };

  return (
    <div data-testid="home-page" style={{ minHeight: "100vh", background: B.bg, color: B.white, fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif", display: "flex", flexDirection: "column" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showNotifs && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setShowNotifs(false)}>
          <div data-testid="notif-modal" onClick={e => e.stopPropagation()} style={{
            background: B.card, borderRadius: 16, padding: 24, border: `1px solid ${B.border}`,
            maxWidth: 440, width: "100%", maxHeight: "70vh", overflowY: "auto",
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, color: B.gold }}>Notifications</h3>
            {notifs.length === 0 ? <p style={{ color: B.gray, fontSize: 13 }}>No notifications yet.</p> :
              notifs.map((n, i) => (
                <div key={i} style={{
                  padding: "10px 14px", marginBottom: 8, borderRadius: 8,
                  background: B.bg, borderLeft: `3px solid ${n.type === "access_granted" ? B.green : B.red}`,
                }}>
                  <div style={{ fontSize: 13, color: B.white }}>{n.message}</div>
                  <div style={{ fontSize: 10, color: B.gray, marginTop: 4 }}>{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                </div>
              ))
            }
            <button onClick={() => setShowNotifs(false)} style={{ marginTop: 12, width: "100%", padding: "8px 0", background: "transparent", border: `1px solid ${B.border}`, borderRadius: 8, color: B.gray, cursor: "pointer", fontSize: 12 }}>Close</button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{ position: "relative", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 32px 0" }}>
        <img src="/flexx-app-icon.png" alt="FLEXX" style={{ height: 48, filter: "brightness(1.1)" }} onError={e => { e.target.style.display = "none"; }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {user ? (
            <>
              <AxelMarketBadge />
              <span style={{ color: B.whiteGray, fontSize: 13 }}>{user.name || user.email}</span>
              {/* Notification bell */}
              <button data-testid="notif-bell" onClick={openNotifs} style={{
                background: notifCount > 0 ? B.gold + "20" : "transparent", border: `1px solid ${notifCount > 0 ? B.gold + "40" : B.border}`,
                borderRadius: 6, color: notifCount > 0 ? B.gold : B.gray, fontSize: 14, padding: "4px 10px", cursor: "pointer", position: "relative",
              }}>
                &#x1F514;
                {notifCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: B.red, color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>{notifCount}</span>}
              </button>
              <LiveNavLink />
              {user.role === "admin" && (
                <button data-testid="admin-link" onClick={() => navigate("/admin")}
                  style={{ background: B.green + "20", border: `1px solid ${B.green}30`, borderRadius: 6, color: B.green, fontSize: 12, padding: "5px 12px", cursor: "pointer", fontWeight: 600 }}>
                  Admin
                </button>
              )}
              <LogoutBtn />
            </>
          ) : (
            <button data-testid="sign-in-btn" onClick={() => setShowAuth(true)}
              style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", fontSize: 13, fontWeight: 700, padding: "10px 24px", cursor: "pointer", letterSpacing: 0.5 }}>
              SIGN IN
            </button>
          )}
        </div>
      </div>

      {/* Hero Section with Background Image (matching flexx-academy.com) */}
      <div style={{ position: "relative", overflow: "hidden", minHeight: 520 }}>
        <div className="hero-bg-wrapper">
          <img className="hero-bg-image" alt="" src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1920&q=80" />
          <div className="hero-bg-overlay" />
          <div className="hero-bg-radial" />
        </div>
        <div className="hero-particles">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`, top: `${Math.random() * 80}%`,
              animationDelay: `${Math.random() * 3}s`, animationDuration: `${3 + Math.random() * 2}s`,
            }} />
          ))}
        </div>
        <div style={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px 48px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(212,168,83,0.08)", border: "1px solid rgba(212,168,83,0.2)", borderRadius: 50, padding: "6px 16px", marginBottom: 24 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={B.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
            <span style={{ fontSize: 12, color: B.gold, fontWeight: 500 }}>Structured Trading Education</span>
          </div>
          <div data-testid="hero-title" style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "-30px 0 -50px", gap: 0 }}>
            <FlexxLogoImg className="flexx-hero-logo" style={{ height: "clamp(180px, 28vw, 420px)" }} />
          </div>
          <p style={{ fontSize: "clamp(18px, 2.5vw, 26px)", fontWeight: 300, margin: "0 0 24px", lineHeight: 1.4, color: B.whiteGray }}>
            Structured Education for <span style={{ color: B.gold }}>AI-Powered Trading</span>
          </p>
          <p style={{ color: B.gray, fontSize: 15, lineHeight: 1.7, margin: "0 0 32px", maxWidth: 600, textAlign: "center" }}>
            From market fundamentals to advanced strategies, prop firm preparation, and modern AI-powered trading ecosystems.
          </p>
          <div style={{ width: "100%", maxWidth: 560, marginBottom: 24, padding: "0 24px", boxSizing: "border-box" }}>
            <SearchLessons user={user} hasAccess={hasAccess} bookmarkIds={bookmarkIds} onToggleBookmark={toggleBookmark} />
          </div>
          <p style={{ color: B.gray, fontSize: 11, opacity: 0.6, margin: 0 }}>
            Education only. No financial advice. No performance guarantees.
          </p>
        </div>
      </div>

      {/* FLEXX Super App v3: Learning Hub (authenticated only) */}
      {user && <FlexxLearningHub user={user} />}

      {/* Course Cards Section */}
      <div style={{ position: "relative", padding: "48px 24px 60px" }}>
        <div className="section-glow top-right" />
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: B.white, margin: "0 0 12px" }}>Choose Your Path</h2>
          <div style={{ height: 2, width: 120, background: `linear-gradient(90deg, transparent, ${B.gold}, transparent)`, margin: "0 auto 12px" }} />
          <p style={{ color: B.gray, fontSize: 14 }}>Select a course to begin your structured learning journey</p>
        </div>
        <div style={{ display: "flex", gap: 24, maxWidth: 1100, margin: "0 auto", flexWrap: "wrap", justifyContent: "center" }}>
          <CourseCard testId="beginner-course-card" title="Beginner & Intermediate" subtitle="FOUNDATIONS"
            icon="&#x1F4DA;" accent={B.blue} accentFaint={B.blueFaint}
            locked={user && !hasAccess("beginner")}
            description="Build your trading foundation from the ground up. Learn market structure, price action, risk management, and the FLEXX S.P.E.R. methodology across two market tracks."
            features={["Crypto & Stocks Track", "Forex & Indices Track", "Beginner to Intermediate progression", "Strategy Cards & Practice Tasks", "S.P.E.R. Framework (Scan, Plan, Execute, Review)"]}
            buttonText="Start Learning" onClick={() => go("/beginner", "beginner")} />
          <CourseCard testId="advanced-course-card" title="Advanced Program" subtitle="MASTERY"
            icon="&#x1F680;" accent={B.gold} accentFaint={B.goldFaint}
            locked={user && !hasAccess("advanced")}
            description="Take your skills to the next level with advanced strategies, DEX trading, algorithmic concepts, prop firm challenges, and coaching materials for team development."
            features={["Crypto Advanced (DEX, DeFi, On-Chain)", "Forex Advanced (Algos, Multi-TF, Sessions)", "Prop Firm Path (Challenges, Scaling)", "Coach Materials (Team Building, Reviews)", "Advanced Risk & Portfolio Management"]}
            buttonText="Go Advanced" onClick={() => go("/advanced", "advanced")} />
          <CourseCard testId="cfd-course-card" title="CFD Mastery" subtitle="CONTRACTS FOR DIFFERENCE"
            icon="&#x25C8;" accent={B.cfd} accentFaint={B.cfdFaint}
            locked={user && !hasAccess("cfd")}
            description="Master Contracts for Difference — the professional's instrument. Trade stocks, indices, commodities, forex, and crypto from a single account with leverage and the ability to profit in both directions."
            features={["CFD Fundamentals & Risk Architecture", "Market Structure for All CFD Markets", "4 Pro Strategies (Index, Gold, Stock, Short)", "Multi-Asset Portfolio & Hedging", "Trading Business & Tax Considerations"]}
            buttonText="Start CFD Mastery" onClick={() => go("/cfd", "cfd")} />
          <CourseCard testId="commodities-course-card" title="Commodities & Futures" subtitle="GOLD · OIL · BTC · FUTURES"
            icon="&#x2B22;" accent={B.commodities} accentFaint={B.commoditiesFaint}
            locked={user && !hasAccess("commodities")}
            description="Trade Gold, Oil, BTC, and traditional futures across three execution wrappers: broker CFDs on MT5, tokenized spot, and perpetual futures. The 24/7 Trader's Edge."
            features={["Gold — 3 Ways to Trade (CFD, Tokenized, Perps)", "Oil — WTI, Brent & The 24/7 Revolution", "BTC — CFD vs Spot vs Perpetuals", "Traditional Futures (CME) — ES, NQ, GC, CL", "Prop Firm Path & FLEXX S.P.E.R. Execution"]}
            buttonText="Start Commodities" onClick={() => go("/commodities", "commodities")} />
          <CourseCard testId="roadmap-course-card" title="3-Month Roadmap" subtitle="INTERMEDIATE → EXPERT"
            icon="&#x1F5FA;" accent="#9B59B6" accentFaint="rgba(155,89,182,0.09)"
            locked={user && !hasAccess("roadmap")}
            description="A structured 3-month skill acquisition roadmap taking you from intermediate trader to prop firm-funded expert. Week-by-week milestones, daily routines, and scaling plans."
            features={["Month 1: Foundation Mastery & Risk Management", "Month 2: Advanced Strategy & EA Integration", "Month 3: Prop Firm Challenge & Scaling", "Weekly Milestones & Daily Routines", "Prop Firm Comparison & Rules Guide"]}
            buttonText="Start Roadmap" onClick={() => go("/roadmap", "roadmap")} />
        </div>
      </div>

      {/* Bookmarks */}
      {user && bookmarks.length > 0 && (
        <BookmarksPanel bookmarks={bookmarks} onRemove={(b) => toggleBookmark({ id: b.lesson_id, course: b.course, title: b.title, tag: b.tag, track: b.track, phaseLabel: b.phase_label, duration: b.duration })} />
      )}

      {/* Stats */}
      <div style={{ display: "flex", justifyContent: "center", gap: 48, padding: "32px 24px 40px", borderTop: `1px solid ${B.border}`, maxWidth: 700, margin: "0 auto", flexWrap: "wrap" }}>
        {[{ label: "Modules", value: "100+" }, { label: "Strategy Cards", value: "15+" }, { label: "Market Tracks", value: "8" }, { label: "Courses", value: "5" }].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: B.gold }}>{s.value}</div>
            <div style={{ fontSize: 11, color: B.gray, letterSpacing: 1, fontFamily: "monospace" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 24px 48px", borderTop: `1px solid ${B.border}`, background: "linear-gradient(180deg, transparent, rgba(10,10,15,0.5))" }}>
        <FlexxLogoImg data-testid="flexx-logo-footer"
          style={{ height: 64, marginBottom: 16, filter: "brightness(0.9)" }} />
        <div style={{ height: 1, width: 80, background: `linear-gradient(90deg, transparent, ${B.gold}40, transparent)`, marginBottom: 16 }} />
        <div style={{ color: B.gray, fontSize: 12, textAlign: "center", lineHeight: 1.6 }}>
          FLEXX Trading Academy &mdash; Education First. Structure Always.
        </div>
        <div style={{ color: B.gray, fontSize: 10, marginTop: 8, opacity: 0.5, textAlign: "center" }}>
          Education and collaboration only. No financial advice. No performance guarantees. No brokerage services.
        </div>
      </div>
    </div>
  );
};

const LogoutBtn = () => {
  const { logout } = useAuth();
  return <button data-testid="home-logout-btn" onClick={logout} style={{ background: "transparent", border: "1px solid #1E222A", borderRadius: 6, color: "#6B7280", fontSize: 12, padding: "5px 12px", cursor: "pointer" }}>Logout</button>;
};

/* ====== Navigation Bar ====== */
const NavBackForward = () => {
  return (
    <div data-testid="nav-back-forward-bar" style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9000,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 20px",
      background: "rgba(13,15,19,0.95)", backdropFilter: "blur(12px)",
      borderBottom: `1px solid ${B.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button data-testid="nav-back-btn" onClick={() => window.history.back()}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            background: B.gold + "15", border: `1px solid ${B.gold}30`,
            borderRadius: 8, padding: "7px 14px",
            color: B.gold, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          Back
        </button>
        <button data-testid="nav-forward-btn" onClick={() => window.history.forward()}
          style={{
            display: "flex", alignItems: "center", gap: 4,
            background: "transparent", border: `1px solid ${B.border}`,
            borderRadius: 8, padding: "7px 14px",
            color: B.gray, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
          Forward
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>
      <Link to="/" style={{ display: "flex", alignItems: "center" }}>
        <img src="/flexx-app-icon.png" alt="FLEXX" style={{ height: 32, filter: "brightness(0.9)" }} onError={e => { e.target.style.display = "none"; }} />
      </Link>
    </div>
  );
};

/* ====== Course Gate ====== */
// ── LIVE nav pill in the header — polls /api/live/state every 45s so the
//    red dot pulses only when admin toggles LIVE ON.
const LiveNavLink = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLive, setIsLive] = React.useState(false);
  React.useEffect(() => {
    if (!user) return undefined;
    let mounted = true;
    const fetchLive = async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        if (!token) return;
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/live/state`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (mounted) setIsLive(!!res.data?.is_live);
      } catch (_) { /* silent */ }
    };
    fetchLive();
    const i = setInterval(fetchLive, 45_000);
    return () => { mounted = false; clearInterval(i); };
  }, [user]);
  return (
    <button data-testid="nav-live-link" onClick={() => navigate("/live")}
      style={{
        background: isLive ? "rgba(231,76,60,0.12)" : "transparent",
        border: `1px solid ${isLive ? B.red + "80" : B.border}`,
        borderRadius: 6, color: isLive ? B.red : B.whiteGray,
        fontSize: 11, padding: "5px 10px", cursor: "pointer",
        fontFamily: "monospace", letterSpacing: 1.5, fontWeight: 700,
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
      <span
        data-testid={isLive ? "nav-live-dot-on" : "nav-live-dot-off"}
        className={isLive ? "flexx-pulse-red" : ""}
        style={{ width: 7, height: 7, borderRadius: "50%", background: isLive ? B.red : B.gray, display: "inline-block" }} />
      LIVE
    </button>
  );
};

// Placeholder for the future Curriculum Map (Phase 2C). Shows a simple
// card with the 5 tier names + "Coming soon" notice so the bottom-nav
// Learn tab has a valid destination today.
const LearnPlaceholderGuarded = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();
  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg data-testid="learn-gate-logo" style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <button data-testid="learn-gate-signin-btn" onClick={() => setShowAuth(true)}
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  const tiers = [
    { id: "beginner",    label: "Tier 1 — Beginner",     color: B.gold,    path: "/beginner",    desc: "Foundations: mindset, market structure, risk, psychology" },
    { id: "advanced",    label: "Tier 2 — Intermediate", color: B.gold,    path: "/advanced",    desc: "Full SuPER system: 5 steps of the daily routine" },
    { id: "roadmap",     label: "Tier 3 — Roadmap",      color: "#00E5A0", path: "/roadmap",     desc: "90-day execution plan + trading journal flywheel" },
    { id: "cfd",         label: "Tier 4 — CFD",          color: "#FF6B35", path: "/cfd",         desc: "Leveraged derivatives: Indices, metals, energy" },
    { id: "commodities", label: "Tier 5 — Commodities",  color: "#B57BFF", path: "/commodities", desc: "Futures, seasonality, macro correlations" },
  ];
  return (
    <div data-testid="learn-curriculum-map" style={{ minHeight: "100vh", background: B.bg, color: "#EDE8DC", padding: "40px 20px 80px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 6 }}>
          <FlexxLogoImg style={{ height: 40, filter: `drop-shadow(0 0 12px ${B.gold}55)` }} />
          <div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: B.gold, fontFamily: "monospace" }}>FLEXX ACADEMY</div>
            <h1 style={{ fontSize: 38, fontWeight: 800, margin: 0, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", letterSpacing: -0.5 }}>CURRICULUM MAP</h1>
          </div>
        </div>
        <p style={{ color: "#AAAAAA", marginTop: 14, maxWidth: 720, lineHeight: 1.6, fontSize: 14 }}>
          Five tiers · ~50 lessons · ~6-month path from absolute beginner to fully SuPER-certified trader. Pick a tier to jump into the existing course materials, or run through the structured Tier 1 lessons below.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14, marginTop: 28 }}>
          {tiers.map((t) => (
            <button key={t.id} data-testid={`learn-tier-${t.id}`} onClick={() => navigate(t.path)}
              style={{
                all: "unset", cursor: "pointer",
                background: "#0C0C0C", border: `1px solid ${B.border}`,
                borderLeft: `3px solid ${t.color}`, borderRadius: 10,
                padding: 18,
              }}>
              <div style={{ fontSize: 11, color: t.color, fontFamily: "monospace", letterSpacing: 1.5, marginBottom: 6 }}>{t.label}</div>
              <div style={{ fontSize: 14, color: "#EDE8DC", lineHeight: 1.5 }}>{t.desc}</div>
              <div style={{ fontSize: 11, color: B.gold, marginTop: 10, fontFamily: "monospace", letterSpacing: 1 }}>OPEN →</div>
            </button>
          ))}
        </div>

        {/* Tier 1 + Tier 2 structured lessons (b1-1 → t2-5) with quizzes */}
        <LessonsHub />
      </div>
    </div>
  );
};

const LiveRoomGuarded = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg data-testid="live-gate-logo" style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <p style={{ color: B.whiteGray, marginBottom: 20 }}>Please sign in to access the FLEXX Live Room.</p>
        <button data-testid="live-gate-signin-btn" onClick={() => setShowAuth(true)}
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  return (
    <>
      <NavBackForward />
      <div style={{ paddingTop: 52 }}>
        <LiveRoom />
      </div>
    </>
  );
};

const AcademyHubGuarded = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg data-testid="hub-gate-logo" style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <p style={{ color: B.whiteGray, marginBottom: 20 }}>Please sign in to access the Academy Hub.</p>
        <button data-testid="hub-gate-signin-btn" onClick={() => setShowAuth(true)}
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  return (
    <>
      <NavBackForward />
      <div style={{ paddingTop: 52 }}>
        <AcademyHub />
      </div>
    </>
  );
};

const IntelHubGuarded = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <p style={{ color: B.whiteGray, marginBottom: 20 }}>Please sign in to access AXEL Intel Hub.</p>
        <button onClick={() => setShowAuth(true)} data-testid="intel-gate-signin-btn"
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  // Premium gate — when the global lock is enabled and the user hasn't been
  // individually unlocked (paid / funded-account holder), show a paywall
  // instead of the hub. Admins always bypass this.
  if (user.intel_unlocked === false) return (
    <div data-testid="intel-locked-screen" style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{ fontSize: 48, marginBottom: 18 }}>🔒</div>
        <div style={{ fontSize: 9, letterSpacing: 3, color: B.gold, fontFamily: "monospace", marginBottom: 10 }}>PREMIUM FEATURE</div>
        <h2 style={{ color: B.white, fontSize: 22, marginBottom: 12 }}>AXEL Intel Hub is locked</h2>
        <p style={{ color: B.whiteGray, marginBottom: 22, lineHeight: 1.65, fontSize: 14 }}>
          Daily Brief, Research Desk and live macro intelligence are reserved for funded-account holders and paid members. Reach out on Telegram to unlock your account.
        </p>
        <a href="https://t.me/flexxacademy" target="_blank" rel="noopener noreferrer" data-testid="intel-locked-telegram-btn"
          style={{ display: "inline-block", background: B.gold, borderRadius: 8, color: "#000", padding: "12px 28px", fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
          Request Access
        </a>
        <div style={{ marginTop: 28, padding: "10px 14px", background: `${B.gold}10`, border: `1px solid ${B.gold}33`, borderRadius: 8, color: B.whiteGray, fontSize: 12, lineHeight: 1.6 }}>
          Signed in as <b style={{ color: B.gold }}>{user.email}</b>
        </div>
      </div>
    </div>
  );
  return <AxelIntelHub />;
};

const ToolsHubGuarded = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg data-testid="tools-gate-logo" style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <p style={{ color: B.whiteGray, marginBottom: 20 }}>Please sign in to access FLEXX Trading Tools.</p>
        <button data-testid="tools-gate-signin-btn" onClick={() => setShowAuth(true)}
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  return (
    <>
      <NavBackForward />
      <div style={{ paddingTop: 52 }}>
        <ToolsHub />
      </div>
    </>
  );
};

const CourseGate = ({ courseId, courseLabel, children }) => {
  const { user, loading, hasAccess } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  if (loading) return <div style={{ minHeight: "100vh", background: B.bg }} />;
  if (!user) return (
    <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      <div style={{ textAlign: "center" }}>
        <FlexxLogoImg data-testid="course-gate-logo" style={{ height: 64, marginBottom: 20, filter: `drop-shadow(0 0 18px ${B.gold}55)` }} />
        <h2 style={{ color: B.white, marginBottom: 8 }}>Sign In Required</h2>
        <p style={{ color: B.whiteGray, marginBottom: 20 }}>Please sign in to access the {courseLabel}.</p>
        <button data-testid="gate-signin-btn" onClick={() => setShowAuth(true)}
          style={{ background: B.gold, border: "none", borderRadius: 8, color: "#000", padding: "12px 32px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          Sign In
        </button>
      </div>
    </div>
  );
  if (!hasAccess(courseId)) return <AccessDenied courseLabel={courseLabel} />;
  return children;
};

/* ====== Global Flexx Overlays (AXEL + XP toasts) — only when authenticated AND not on public pages ====== */
const FlexxGlobalOverlays = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  // Hide on public/unauthenticated pages even if a stale token is still around
  const publicPaths = ["/verify/", "/reset-password"];
  if (publicPaths.some((p) => location.pathname.startsWith(p))) return null;
  return (
    <>
      <XPNotifications />
      <AxelWidget />
    </>
  );
};

/* ====== Meta Pixel SPA route tracker ======
 * Base pixel in index.html already fires PageView on hard load. This component
 * re-fires PageView on every React Router navigation so client-side route
 * changes also register as page views in Meta Events Manager. */
const MetaPixelRouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, location.search]);
  return null;
};

/* ====== App ====== */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MetaPixelRouteTracker />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/intel" element={<IntelHubGuarded />} />
          <Route path="/live" element={
            <LiveRoomGuarded />
          } />
          <Route path="/library" element={
            <AcademyHubGuarded />
          } />
          <Route path="/learn" element={
            <LearnPlaceholderGuarded />
          } />
          <Route path="/tools" element={
            <ToolsHubGuarded />
          } />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify/:certId" element={<CertificateVerifyPage />} />
          <Route path="/certificate-demo" element={<CertificateDemo />} />
          <Route path="/beginner" element={
            <CourseGate courseId="beginner" courseLabel="Beginner & Intermediate Course">
              <NavBackForward />
              <div style={{ paddingTop: 52 }}>
                <FlexxBeginnerCourse />
              </div>
            </CourseGate>
          } />
          <Route path="/advanced" element={
            <CourseGate courseId="advanced" courseLabel="Advanced Program">
              <NavBackForward />
              <div style={{ paddingTop: 52 }}>
                <FlexxAdvancedCourse />
              </div>
            </CourseGate>
          } />
          <Route path="/cfd" element={
            <CourseGate courseId="cfd" courseLabel="CFD Mastery">
              <NavBackForward />
              <div style={{ paddingTop: 52 }}>
                <FlexxCFDCourse />
              </div>
            </CourseGate>
          } />
          <Route path="/commodities" element={
            <CourseGate courseId="commodities" courseLabel="Commodities & Futures">
              <NavBackForward />
              <div style={{ paddingTop: 52 }}>
                <FlexxCommoditiesCourse />
              </div>
            </CourseGate>
          } />
          <Route path="/roadmap" element={
            <CourseGate courseId="roadmap" courseLabel="3-Month Trading Roadmap">
              <NavBackForward />
              <div style={{ paddingTop: 52 }}>
                <FlexxRoadmap />
              </div>
            </CourseGate>
          } />
        </Routes>
        <FlexxGlobalOverlays />
        <BottomNav />
        <PwaShell />
        <PwaSplash />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
